import type {
  MergedOnboardingFlow,
  MergedTourStep,
  TourCopyFields,
  TourStructureDefinition,
} from './types.js';
import {
  coerceEventListingPlainText,
  coerceTourPlainText,
} from '../utils/coerceEventListingText.js';

export type CopyByStep = Record<string, TourCopyFields>;

function coerceTourCopyField(value: unknown, fallback: string): string {
  return coerceEventListingPlainText(value, fallback);
}

export function mergeTourDefinition(
  structure: TourStructureDefinition,
  copyByStep: CopyByStep,
  options?: { active?: boolean },
): MergedOnboardingFlow {
  const steps: MergedTourStep[] = structure.steps.map((step) => {
    const copy = copyByStep[step.stepName];
    return {
      stepName: step.stepName,
      target: step.target,
      placement: step.placement,
      disableBeacon: step.disableBeacon,
      beforeAction: step.beforeAction,
      title: copy?.title != null ? coerceTourCopyField(copy.title, step.stepName) : step.stepName,
      content: copy?.content != null ? coerceTourCopyField(copy.content, '') : '',
      tip: copy?.tip != null ? coerceTourCopyField(copy.tip, '') : undefined,
      helpUrl: copy?.helpUrl != null ? coerceTourCopyField(copy.helpUrl, '') : undefined,
      ctaLabel: copy?.ctaLabel != null ? coerceTourCopyField(copy.ctaLabel, '') : undefined,
    };
  });

  return {
    id: structure.slug,
    name: coerceTourPlainText(structure.name, structure.slug),
    description: coerceTourPlainText(structure.description, ''),
    tourType: structure.tourType,
    requiredRole:
      structure.requiredRoles.length > 0 ? structure.requiredRoles : undefined,
    triggers: structure.triggers,
    prerequisites: structure.prerequisites,
    active: options?.active ?? true,
    estimatedDurationMinutes: structure.estimatedDurationMinutes,
    steps,
  };
}

export function mergeTourDefinitions(
  structures: TourStructureDefinition[],
  copyByTour: Record<string, CopyByStep>,
  activeBySlug: Record<string, boolean> = {},
): MergedOnboardingFlow[] {
  return structures.map((structure) =>
    mergeTourDefinition(structure, copyByTour[structure.slug] ?? {}, {
      active: activeBySlug[structure.slug] ?? true,
    }),
  );
}
